import { FormikContextTypeEnhanced } from '@superdispatch/forms';
import { useResponsiveValue, useUID } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { Form, FormikProvider, FormikValues } from 'formik';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import { autoCloseWebView } from 'shared/helpers/WebViewHelpers';
import { PageHeader, PageLayout } from 'shared/layout/PageLayout';
import { BackButton } from 'shared/ui/BackButton';
import { basePath } from 'shared/utils/RouterUtils';
import styled from 'styled-components';

const CenterBox = styled(Box)`
  justify-content: center;
`;

interface SettingsPageLayoutProps {
  loading?: boolean;
  title?: ReactNode;
  titleID?: string;
  actions?: ReactNode;
  children?: ReactNode;
  stickyHeader?: boolean;
}

export function SettingsPageLayout({
  title,
  titleID,
  actions,
  loading,
  children,
  stickyHeader,
}: SettingsPageLayoutProps) {
  const navigate = useNavigate();
  const isMobile = useResponsiveValue(true, true, false);
  const [{ back_url }] = useQuery();

  const handleBackClick = () => {
    if (isWebView && back_url) {
      const url = back_url.startsWith(basePath)
        ? back_url.slice(basePath.length)
        : back_url;
      navigate(url, { replace: true });
    } else if (isWebView) {
      autoCloseWebView(0);
    } else {
      navigate('/settings', { replace: true });
    }
  };

  return (
    <PageLayout
      loading={loading}
      stickyHeader={stickyHeader}
      header={
        <PageHeader
          title={title}
          titleID={titleID}
          endActions={actions}
          startAction={
            isMobile && !isWebView && <BackButton onClick={handleBackClick} />
          }
        />
      }
    >
      <CenterBox display="flex">{children}</CenterBox>
      {isMobile && <Box width="100%" height="50px" />}
    </PageLayout>
  );
}

interface SettingsPageFormProps {
  loading?: boolean;
  formik: FormikContextTypeEnhanced<FormikValues, unknown>;
  title?: ReactNode;
  children?: ReactNode;
  stickyHeader?: boolean;
  disabledSubmit?: boolean;
}

export function SettingsPageForm({
  title,
  formik,
  loading,
  children,
  stickyHeader,
  disabledSubmit,
}: SettingsPageFormProps) {
  const titleID = useUID();
  const isLoading = loading || formik.isSubmitting;

  return (
    <FormikProvider value={formik}>
      <Form aria-labelledby={titleID}>
        <SettingsPageLayout
          title={title}
          titleID={titleID}
          loading={isLoading}
          stickyHeader={stickyHeader}
          actions={
            <Button
              type="submit"
              pending={isLoading}
              disabled={!formik.dirty || disabledSubmit}
            >
              Save
            </Button>
          }
        >
          <Box maxWidth="512px" width="100%">
            {children}
          </Box>
        </SettingsPageLayout>
      </Form>
    </FormikProvider>
  );
}
